import { useCostDashboard } from "@/hooks/useCostDashboard";
import {
  Alert,
  Badge,
  Button,
  Card,
  Group,
  Paper,
  Progress,
  RingProgress,
  SimpleGrid,
  Skeleton,
  Stack,
  Table,
  Text,
  Title,
  Tooltip,
} from "@mantine/core";
import {
  IconAlertTriangle,
  IconArrowRight,
  IconChartBar,
  IconCoin,
  IconContract,
  IconFileInvoice,
  IconTrendingDown,
  IconTrendingUp,
} from "@tabler/icons-react";
import { createFileRoute, useNavigate } from "@tanstack/react-router";

export const Route = createFileRoute("/projects/$projectId/cost-dashboard" as any)({
  component: CostDashboardPage,
});

function CostDashboardPage() {
  const params = Route.useParams();
  const projectId = "projectId" in params ? params.projectId : "";
  const navigate = useNavigate();
  const { data, isLoading, error } = useCostDashboard(projectId);

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
    });
  };

  const getStatusColor = (status: "under" | "at" | "over") => {
    switch (status) {
      case "under":
        return "green";
      case "at":
        return "yellow";
      case "over":
        return "red";
      default:
        return "gray";
    }
  };

  const getHealthColor = (health: "healthy" | "warning" | "critical") => {
    switch (health) {
      case "healthy":
        return "green";
      case "warning":
        return "yellow";
      case "critical":
        return "red";
      default:
        return "gray";
    }
  };

  // Color palette for divisions
  const divisionColors = [
    "#339af0", // blue
    "#51cf66", // green
    "#ff6b6b", // red
    "#ffd43b", // yellow
    "#845ef7", // violet
    "#20c997", // teal
    "#f06595", // pink
    "#fab005", // orange
  ];

  if (isLoading) {
    return (
      <Stack gap="lg">
        <SimpleGrid cols={{ base: 1, sm: 2, md: 4 }}>
          <Skeleton height={120} radius="md" />
          <Skeleton height={120} radius="md" />
          <Skeleton height={120} radius="md" />
          <Skeleton height={120} radius="md" />
        </SimpleGrid>
        <Skeleton height={300} radius="md" />
        <Skeleton height={400} radius="md" />
      </Stack>
    );
  }

  if (error || !data) {
    return (
      <Paper p="xl" withBorder>
        <Text c="dimmed">Failed to load cost dashboard data</Text>
      </Paper>
    );
  }

  const { summary, byDivision, byCostCode, recentCommitments } = data;
  const hasBudget = summary.totalBudget > 0;
  const hasCommitments = summary.totalCommitted > 0;
  const hasActuals = summary.totalActuals > 0;

  // Calculate margin projection
  const originalMarginPercent = summary.totalBudget > 0 ? 8 : 0; // Assuming 8% original margin
  const originalMarginAmount = summary.totalBudget * (originalMarginPercent / 100);
  const currentMarginAmount = summary.totalVariance;
  const currentMarginPercent = summary.totalBudget > 0
    ? (currentMarginAmount / summary.totalBudget) * 100
    : 0;
  const isMarginDeclining = currentMarginPercent < originalMarginPercent;

  // Identify cost codes that need attention
  const attentionNeeded = byCostCode.filter(cc => {
    const isOverBudget = cc.status === "over";
    const isUnderCommitted = cc.budget > 0 && cc.committed < (cc.budget * 0.8);
    const isTrendingOver = cc.committed > 0 &&
                          cc.actuals > (cc.committed * 0.9) &&
                          cc.actuals < cc.committed; // Not yet complete
    return isOverBudget || (isUnderCommitted && cc.budget > 10000) || isTrendingOver;
  }).sort((a, b) => {
    // Sort by severity: over budget first, then by amount
    if (a.status === "over" && b.status !== "over") return -1;
    if (b.status === "over" && a.status !== "over") return 1;
    return Math.abs(b.variance) - Math.abs(a.variance);
  }).slice(0, 5); // Top 5 issues

  // Get problem divisions for margin trend
  const problemDivisions = byDivision
    .filter(div => div.status === "over" || div.status === "at")
    .map(div => div.division.split(" ")[0]) // Get division number
    .join(", ");

  return (
    <Stack gap="lg">
      {/* PROJECT HEALTH CARD */}
      {hasBudget && (
        <Card
          withBorder
          padding="xl"
          style={{
            borderWidth: 2,
            borderColor: getHealthColor(summary.healthStatus) === "green"
              ? "var(--mantine-color-green-6)"
              : getHealthColor(summary.healthStatus) === "yellow"
              ? "var(--mantine-color-yellow-6)"
              : "var(--mantine-color-red-6)"
          }}
        >
          <Stack gap="lg">
            {/* Header */}
            <Group justify="space-between" align="center">
              <Title order={2}>PROJECT HEALTH</Title>
              <Badge
                size="lg"
                variant="filled"
                color={getHealthColor(summary.healthStatus)}
                leftSection={<span style={{ fontSize: '16px' }}>⚡</span>}
              >
                LIVE
              </Badge>
            </Group>

            {/* Main Metrics */}
            <SimpleGrid cols={{ base: 1, sm: 2, md: 4 }} spacing="xl">
              {/* Contract */}
              <Stack gap="xs">
                <Text size="sm" c="dimmed" fw={600} tt="uppercase">
                  Contract
                </Text>
                <Text size="xl" fw={700}>
                  {formatCurrency(summary.totalBudget)}
                </Text>
                <Progress
                  value={100}
                  color="blue"
                  size="lg"
                  radius="sm"
                />
              </Stack>

              {/* Committed */}
              <Stack gap="xs">
                <Text size="sm" c="dimmed" fw={600} tt="uppercase">
                  Committed
                </Text>
                <Text size="xl" fw={700} c={summary.percentCommitted > 100 ? "red" : undefined}>
                  {formatCurrency(summary.totalCommitted)}
                </Text>
                <Progress
                  value={Math.min(summary.percentCommitted, 100)}
                  color={summary.percentCommitted > 100 ? "red" : summary.percentCommitted > 90 ? "yellow" : "orange"}
                  size="lg"
                  radius="sm"
                />
              </Stack>

              {/* Billed (Actuals) */}
              <Stack gap="xs">
                <Text size="sm" c="dimmed" fw={600} tt="uppercase">
                  Billed
                </Text>
                <Text size="xl" fw={700}>
                  {formatCurrency(summary.totalActuals)}
                </Text>
                <Progress
                  value={Math.min(summary.percentSpent, 100)}
                  color="teal"
                  size="lg"
                  radius="sm"
                />
              </Stack>

              {/* Variance */}
              <Stack gap="xs">
                <Text size="sm" c="dimmed" fw={600} tt="uppercase">
                  Variance
                </Text>
                <Group gap="xs" align="baseline">
                  <Text size="xl" fw={700} c={summary.totalVariance >= 0 ? "green" : "red"}>
                    {summary.totalVariance >= 0 ? "+" : ""}
                    {formatCurrency(summary.totalVariance)}
                  </Text>
                  {summary.totalVariance >= 0 && (
                    <Text size="lg" c="green">✓</Text>
                  )}
                </Group>
                <Badge
                  color={getHealthColor(summary.healthStatus)}
                  variant="light"
                  size="lg"
                  tt="uppercase"
                >
                  {summary.healthStatus}
                </Badge>
              </Stack>
            </SimpleGrid>

            {/* ATTENTION NEEDED */}
            {attentionNeeded.length > 0 && (
              <Stack gap="md" mt="md">
                <Group gap="xs">
                  <IconAlertTriangle size={20} style={{ color: "var(--mantine-color-orange-6)" }} />
                  <Text size="lg" fw={600} tt="uppercase">
                    Attention Needed
                  </Text>
                </Group>
                <Card withBorder padding="md" style={{ backgroundColor: "var(--mantine-color-orange-0)" }}>
                  <Stack gap="sm">
                    {attentionNeeded.map(cc => {
                      let reason = "";
                      if (cc.status === "over") {
                        reason = `${formatCurrency(Math.abs(cc.variance))} over budget`;
                      } else if (cc.budget > 0 && cc.committed < (cc.budget * 0.8)) {
                        reason = `Commitment pending for ${formatCurrency(cc.budget - cc.committed)}`;
                      } else if (cc.committed > 0 && cc.actuals > (cc.committed * 0.9)) {
                        reason = `Trending over - ${((cc.actuals / cc.committed) * 100).toFixed(0)}% of commitment spent`;
                      }

                      return (
                        <Group key={cc.costCodeId} justify="space-between" wrap="nowrap">
                          <Group gap="xs" style={{ flex: 1 }}>
                            <Text size="sm" fw={600} style={{ minWidth: '80px' }}>
                              {cc.code}
                            </Text>
                            <Text size="sm" c="dimmed" lineClamp={1}>
                              {cc.name}:
                            </Text>
                            <Text size="sm">{reason}</Text>
                          </Group>
                          {cc.status === "over" && (
                            <Badge color="red" variant="filled" size="sm">
                              OVER
                            </Badge>
                          )}
                        </Group>
                      );
                    })}
                  </Stack>
                </Card>
              </Stack>
            )}

            {/* MARGIN PROJECTION */}
            <Stack gap="md" mt="md">
              <Group gap="xs">
                <IconChartBar size={20} style={{ color: "var(--mantine-color-blue-6)" }} />
                <Text size="lg" fw={600} tt="uppercase">
                  Margin Projection
                </Text>
              </Group>
              <Group align="center" gap="xl">
                <Group gap="sm" align="baseline">
                  <Text size="sm" c="dimmed">Original:</Text>
                  <Text size="lg" fw={600}>
                    {originalMarginPercent.toFixed(1)}% ({formatCurrency(originalMarginAmount)})
                  </Text>
                </Group>
                <IconArrowRight size={20} />
                <Group gap="sm" align="baseline">
                  <Text size="sm" c="dimmed">Current:</Text>
                  <Text
                    size="lg"
                    fw={700}
                    c={currentMarginPercent >= originalMarginPercent ? "green" : currentMarginPercent >= originalMarginPercent * 0.8 ? "yellow" : "red"}
                  >
                    {currentMarginPercent.toFixed(1)}% ({formatCurrency(currentMarginAmount)})
                  </Text>
                </Group>
              </Group>
              <Group gap="xs" align="center">
                <Text size="sm" c="dimmed">Trend:</Text>
                {isMarginDeclining ? (
                  <>
                    <Text size="sm" c="red" fw={600}>↘ Declining</Text>
                    {problemDivisions && (
                      <Text size="sm" c="dimmed">
                        (watch {problemDivisions} divisions)
                      </Text>
                    )}
                  </>
                ) : (
                  <Text size="sm" c="green" fw={600}>↗ Improving</Text>
                )}
              </Group>
            </Stack>
          </Stack>
        </Card>
      )}

      {/* Alert Banner for Over Budget */}
      {summary.overBudgetCount > 0 && (
        <Alert
          icon={<IconAlertTriangle size={20} />}
          title="Budget Warning"
          color="red"
          variant="light"
        >
          {summary.overBudgetCount} cost code{summary.overBudgetCount > 1 ? "s are" : " is"} over
          budget by a total of {formatCurrency(summary.atRiskAmount)}.
        </Alert>
      )}

      {/* Info Banner for Budget Set But No Activity */}
      {hasBudget && !hasCommitments && !hasActuals && (
        <Alert
          icon={<IconChartBar size={20} />}
          title="Ready to Track Costs"
          color="blue"
          variant="light"
        >
          <Text size="sm" mb="xs">
            Your budget is set up with {formatCurrency(summary.totalBudget)} across {summary.costCodeCount} cost codes.
            Start tracking costs by adding commitments and actuals.
          </Text>
          <Text size="xs" c="dimmed">
            <strong>Commitments</strong> are Purchase Orders and Subcontracts that reserve budget.
            <strong> Actuals</strong> track money already spent (invoices paid, materials purchased).
          </Text>
        </Alert>
      )}

      {/* Summary Cards */}
      <SimpleGrid cols={{ base: 1, sm: 2, md: 4 }}>
        <Card withBorder padding="lg">
          <Stack gap="xs">
            <Group gap="xs">
              <IconCoin size={20} style={{ color: "var(--mantine-color-blue-6)" }} />
              <Text size="sm" c="dimmed" fw={500}>
                Total Budget
              </Text>
            </Group>
            <Text size="xl" fw={700}>
              {formatCurrency(summary.totalBudget)}
            </Text>
            <Text size="xs" c="dimmed">
              {summary.costCodeCount} cost codes
            </Text>
          </Stack>
        </Card>

        <Card withBorder padding="lg">
          <Stack gap="xs">
            <Group gap="xs">
              <IconChartBar size={20} style={{ color: "var(--mantine-color-orange-6)" }} />
              <Text size="sm" c="dimmed" fw={500}>
                Committed
              </Text>
            </Group>
            <Text size="xl" fw={700} c={summary.percentCommitted > 100 ? "red" : undefined}>
              {formatCurrency(summary.totalCommitted)}
            </Text>
            <Group gap="xs">
              <Progress
                value={Math.min(summary.percentCommitted, 100)}
                color={summary.percentCommitted > 100 ? "red" : summary.percentCommitted > 80 ? "yellow" : "green"}
                size="sm"
                style={{ flex: 1 }}
              />
              <Text size="xs" c="dimmed">
                {summary.percentCommitted.toFixed(1)}%
              </Text>
            </Group>
          </Stack>
        </Card>

        <Card withBorder padding="lg">
          <Stack gap="xs">
            <Group gap="xs">
              <IconTrendingDown size={20} style={{ color: "var(--mantine-color-teal-6)" }} />
              <Text size="sm" c="dimmed" fw={500}>
                Actual Spent
              </Text>
            </Group>
            <Text size="xl" fw={700}>
              {formatCurrency(summary.totalActuals)}
            </Text>
            <Group gap="xs">
              <Progress
                value={Math.min(summary.percentSpent, 100)}
                color="teal"
                size="sm"
                style={{ flex: 1 }}
              />
              <Text size="xs" c="dimmed">
                {summary.percentSpent.toFixed(1)}%
              </Text>
            </Group>
          </Stack>
        </Card>

        <Card withBorder padding="lg">
          <Stack gap="xs">
            <Group gap="xs">
              {summary.totalVariance >= 0 ? (
                <IconTrendingUp size={20} style={{ color: "var(--mantine-color-green-6)" }} />
              ) : (
                <IconTrendingDown size={20} style={{ color: "var(--mantine-color-red-6)" }} />
              )}
              <Text size="sm" c="dimmed" fw={500}>
                Variance
              </Text>
            </Group>
            <Text size="xl" fw={700} c={summary.totalVariance >= 0 ? "green" : "red"}>
              {summary.totalVariance >= 0 ? "+" : ""}
              {formatCurrency(summary.totalVariance)}
            </Text>
            <Badge color={getHealthColor(summary.healthStatus)} variant="light" size="sm">
              {summary.healthStatus}
            </Badge>
          </Stack>
        </Card>
      </SimpleGrid>

      {!hasBudget ? (
        <Paper p="xl" withBorder style={{ borderStyle: "dashed", borderColor: "var(--mantine-color-blue-3)" }}>
          <Stack gap="lg" align="center" py="xl">
            <div style={{
              padding: "20px",
              borderRadius: "50%",
              backgroundColor: "var(--mantine-color-blue-0)"
            }}>
              <IconChartBar size={48} style={{ color: "var(--mantine-color-blue-6)" }} />
            </div>

            <Stack gap="sm" align="center">
              <Title order={2}>Cost Dashboard</Title>
              <Text c="dimmed" ta="center" maw={600} size="md">
                Track your project's financial health with real-time budget vs. actual analysis
              </Text>
            </Stack>

            <SimpleGrid cols={{ base: 1, sm: 3 }} spacing="lg" w="100%" maw={700} mt="md">
              <Card withBorder padding="md" style={{ borderColor: "var(--mantine-color-blue-2)" }}>
                <Stack gap="xs" align="center">
                  <IconCoin size={24} style={{ color: "var(--mantine-color-blue-6)" }} />
                  <Text size="sm" fw={600}>Budget</Text>
                  <Text size="xs" c="dimmed" ta="center">
                    Your planned costs by division and cost code
                  </Text>
                </Stack>
              </Card>

              <Card withBorder padding="md" style={{ borderColor: "var(--mantine-color-orange-2)" }}>
                <Stack gap="xs" align="center">
                  <IconContract size={24} style={{ color: "var(--mantine-color-orange-6)" }} />
                  <Text size="sm" fw={600}>Commitments</Text>
                  <Text size="xs" c="dimmed" ta="center">
                    POs and subcontracts that reserve budget
                  </Text>
                </Stack>
              </Card>

              <Card withBorder padding="md" style={{ borderColor: "var(--mantine-color-teal-2)" }}>
                <Stack gap="xs" align="center">
                  <IconFileInvoice size={24} style={{ color: "var(--mantine-color-teal-6)" }} />
                  <Text size="sm" fw={600}>Actuals</Text>
                  <Text size="xs" c="dimmed" ta="center">
                    Money already spent on invoices and materials
                  </Text>
                </Stack>
              </Card>
            </SimpleGrid>

            <Stack gap="sm" align="center" mt="md">
              <Button
                size="lg"
                variant="filled"
                leftSection={<IconCoin size={20} />}
                onClick={() => navigate({ to: "/projects/$projectId/budget", params: { projectId } })}
              >
                Set Up Budget
              </Button>
              <Text size="xs" c="dimmed" ta="center" maw={600}>
                Import your budget to unlock real-time variance tracking, cost code breakdowns, and budget health indicators
              </Text>
            </Stack>
          </Stack>
        </Paper>
      ) : (
        <>
          {/* Division Breakdown */}
          <Paper p="xl" withBorder>
            <Stack gap="lg">
              <Group justify="space-between" align="center">
                <Title order={3}>Budget by Division</Title>
              </Group>

              <SimpleGrid cols={{ base: 1, md: 2 }} spacing="xl">
                {/* Ring Progress Chart */}
                <Card withBorder padding="lg">
                  <Stack gap="md" align="center">
                    <Text size="sm" fw={500}>
                      Commitment Coverage
                    </Text>
                    <RingProgress
                      size={200}
                      thickness={20}
                      sections={[
                        {
                          value: Math.min(summary.percentCommitted, 100),
                          color:
                            summary.percentCommitted > 100
                              ? "red"
                              : summary.percentCommitted > 80
                              ? "yellow"
                              : "green",
                          tooltip: `Committed: ${formatCurrency(summary.totalCommitted)}`,
                        },
                      ]}
                      label={
                        <div style={{ textAlign: "center" }}>
                          <Text size="xl" fw={700}>
                            {summary.percentCommitted.toFixed(0)}%
                          </Text>
                          <Text size="xs" c="dimmed">
                            Committed
                          </Text>
                        </div>
                      }
                    />
                  </Stack>
                </Card>

                {/* Division List */}
                <Card withBorder padding="lg">
                  <Stack gap="md">
                    <Text size="sm" fw={500}>
                      Division Breakdown
                    </Text>
                    <Stack gap="sm">
                      {byDivision.slice(0, 6).map((div, idx) => (
                        <div key={div.division}>
                          <Group justify="space-between" mb={4}>
                            <Group gap="xs">
                              <div
                                style={{
                                  width: 12,
                                  height: 12,
                                  borderRadius: "50%",
                                  backgroundColor: divisionColors[idx % divisionColors.length],
                                }}
                              />
                              <Text size="sm" fw={500}>
                                {div.division}
                              </Text>
                            </Group>
                            <Group gap="xs">
                              <Text size="sm" fw={600}>
                                {formatCurrency(div.committed)}
                              </Text>
                              <Text size="xs" c="dimmed">
                                / {formatCurrency(div.budget)}
                              </Text>
                              <Badge
                                color={getStatusColor(div.status)}
                                variant="light"
                                size="xs"
                              >
                                {div.status}
                              </Badge>
                            </Group>
                          </Group>
                          <Progress
                            value={div.budget > 0 ? Math.min((div.committed / div.budget) * 100, 100) : 0}
                            color={getStatusColor(div.status)}
                            size="sm"
                          />
                        </div>
                      ))}
                    </Stack>
                  </Stack>
                </Card>
              </SimpleGrid>
            </Stack>
          </Paper>

          {/* Cost Code Table */}
          <Paper p="xl" withBorder>
            <Stack gap="lg">
              <Group justify="space-between" align="center">
                <Title order={3}>Cost Code Details</Title>
                <Group gap="xs">
                  <Badge color="green" variant="light" size="sm">
                    Under: {byCostCode.filter((c) => c.status === "under").length}
                  </Badge>
                  <Badge color="yellow" variant="light" size="sm">
                    At Risk: {summary.atBudgetCount}
                  </Badge>
                  <Badge color="red" variant="light" size="sm">
                    Over: {summary.overBudgetCount}
                  </Badge>
                </Group>
              </Group>

              <Table striped highlightOnHover>
                <Table.Thead>
                  <Table.Tr>
                    <Table.Th>Cost Code</Table.Th>
                    <Table.Th>Division</Table.Th>
                    <Table.Th style={{ textAlign: "right" }}>Budget</Table.Th>
                    <Table.Th style={{ textAlign: "right" }}>Committed</Table.Th>
                    <Table.Th style={{ textAlign: "right" }}>Actual</Table.Th>
                    <Table.Th style={{ textAlign: "right" }}>Variance</Table.Th>
                    <Table.Th style={{ textAlign: "center" }}>Status</Table.Th>
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {byCostCode.map((cc) => (
                    <Table.Tr key={cc.costCodeId}>
                      <Table.Td>
                        <Text size="sm" fw={500}>
                          {cc.code}
                        </Text>
                        <Text size="xs" c="dimmed" lineClamp={1}>
                          {cc.name}
                        </Text>
                      </Table.Td>
                      <Table.Td>
                        <Text size="sm">{cc.division}</Text>
                      </Table.Td>
                      <Table.Td style={{ textAlign: "right" }}>
                        <Text size="sm">{formatCurrency(cc.budget)}</Text>
                      </Table.Td>
                      <Table.Td style={{ textAlign: "right" }}>
                        <Tooltip label={`${cc.percentCommitted.toFixed(1)}% of budget`}>
                          <Text size="sm" fw={500}>
                            {formatCurrency(cc.committed)}
                          </Text>
                        </Tooltip>
                      </Table.Td>
                      <Table.Td style={{ textAlign: "right" }}>
                        <Text size="sm">{formatCurrency(cc.actuals)}</Text>
                      </Table.Td>
                      <Table.Td style={{ textAlign: "right" }}>
                        <Text size="sm" c={cc.variance >= 0 ? "green" : "red"} fw={500}>
                          {cc.variance >= 0 ? "+" : ""}
                          {formatCurrency(cc.variance)}
                        </Text>
                      </Table.Td>
                      <Table.Td style={{ textAlign: "center" }}>
                        <Badge color={getStatusColor(cc.status)} variant="light" size="sm">
                          {cc.status}
                        </Badge>
                      </Table.Td>
                    </Table.Tr>
                  ))}
                </Table.Tbody>
              </Table>

              {byCostCode.length === 0 && (
                <Text c="dimmed" ta="center" py="xl">
                  No cost codes with budget data
                </Text>
              )}
            </Stack>
          </Paper>

          {/* Recent Commitments */}
          {recentCommitments.length > 0 && (
            <Paper p="xl" withBorder>
              <Stack gap="lg">
                <Group justify="space-between" align="center">
                  <Title order={3}>Recent Commitments</Title>
                  <Button
                    variant="subtle"
                    rightSection={<IconArrowRight size={16} />}
                    onClick={() =>
                      navigate({ to: "/projects/$projectId/commitments", params: { projectId } })
                    }
                  >
                    View All
                  </Button>
                </Group>

                <SimpleGrid cols={{ base: 1, sm: 2, md: 3 }}>
                  {recentCommitments.map((c) => (
                    <Card key={c.id} withBorder padding="md">
                      <Group justify="space-between" mb="xs">
                        <Group gap="xs">
                          {c.type === "po" ? (
                            <IconFileInvoice size={16} />
                          ) : (
                            <IconContract size={16} />
                          )}
                          <Text size="sm" fw={500}>
                            {c.vendorName}
                          </Text>
                        </Group>
                        <Text size="xs" c="dimmed">
                          {formatDate(c.date)}
                        </Text>
                      </Group>
                      <Group justify="space-between">
                        <Text size="sm" c="dimmed">
                          {c.costCode}
                        </Text>
                        <Text size="sm" fw={600}>
                          {formatCurrency(c.amount)}
                        </Text>
                      </Group>
                    </Card>
                  ))}
                </SimpleGrid>
              </Stack>
            </Paper>
          )}
        </>
      )}

      {/* Quick Actions */}
      <Paper p="xl" withBorder>
        <Stack gap="lg">
          <Title order={3}>Quick Actions</Title>
          <SimpleGrid cols={{ base: 1, sm: 2, md: 4 }}>
            <Button
              variant="light"
              leftSection={<IconFileInvoice size={18} />}
              onClick={() =>
                navigate({ to: "/projects/$projectId/commitments", params: { projectId } })
              }
              size="md"
            >
              Add Commitment
            </Button>
            <Button
              variant="light"
              leftSection={<IconCoin size={18} />}
              onClick={() =>
                navigate({ to: "/projects/$projectId/budget", params: { projectId } })
              }
              size="md"
            >
              View Budget
            </Button>
          </SimpleGrid>
        </Stack>
      </Paper>
    </Stack>
  );
}
